// One self-contained assignment decision; completion marks each shift in place.
import { motion, useReducedMotion } from 'motion/react';
import { type ReactNode, useId } from 'react';

import { getAvatarInitials } from '@apps/web/components/ui/user_avatar';
import { useEntityReferenceLabel } from '@apps/web/features/phoebe_chat_v3/api/entity_references';
import { parseReferenceTokens } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { useHarnessScope } from '@apps/web/features/phoebe_chat_v3/ui/harness_scope_context';
import {
    CARD_SURFACES,
    RECEIPT_PADDING,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/compact_receipt';
import { FlowerMark } from '@apps/web/features/phoebe_chat_v3/ui/primitives/flower_mark';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { cn } from '@apps/web/lib/utils';

export type AssignFillShift = Readonly<{
    readonly id: string;
    readonly name: string;
    readonly detail: string;
    readonly recurrenceLabel?: string;
    readonly resultLabel?: string;
}>;

export type AssignFillStatus =
    | 'pending'
    | 'assigning'
    | 'assigned'
    | 'partial'
    | 'failed'
    | 'denied';

export type AssignFillCardProps = Readonly<{
    readonly caregiverName: string;
    readonly shifts: ReadonlyArray<AssignFillShift>;
    readonly statusLabel?: string;
    // Non-shift-write consumers keep Assign; shift writes override it.
    readonly confirmLabel?: string;
    readonly notices?: ReadonlyArray<string>;
    // The Details disclosure belongs to the card, not below it.
    readonly footer?: ReactNode;
    readonly disabled?: boolean;
}> &
    (
        | Readonly<{
              status: 'pending';
              onAssign: () => void;
              onDeny: () => void;
          }>
        | Readonly<{
              status: Exclude<AssignFillStatus, 'pending'>;
              onAssign?: () => void;
              onDeny?: () => void;
          }>
    );

const STATUS_LABELS: Readonly<Record<AssignFillStatus, string>> = {
    pending: 'Assign',
    assigning: 'Assigning…',
    assigned: 'Assigned',
    partial: 'Some shifts were not assigned',
    failed: 'Not assigned',
    denied: 'Not assigned',
};
const PARTICLES = Array.from({ length: 12 }, (_, index) => ({
    left: `${8 + (index / 11) * 84}%`,
    top: `${24 + (index % 3) * 22}%`,
    drift: (index % 2 === 0 ? -1 : 1) * (10 + (index % 4) * 4),
    color: [
        'var(--agent-action)',
        'var(--agent-action-strong)',
        'var(--agent-action-border)',
    ][index % 3],
    rotation: index % 2 === 0 ? -110 : 130,
    delay: (index % 4) * 0.035,
}));

export function AssignFillCard({
    caregiverName,
    shifts,
    status,
    statusLabel,
    confirmLabel = 'Assign',
    notices = [],
    footer,
    onAssign,
    onDeny,
    disabled = false,
}: AssignFillCardProps): ReactNode {
    const assigned = status === 'assigned';
    const pending = status === 'pending';
    const reduceMotion = useReducedMotion();
    const titleId = useId();
    const scope = useHarnessScope();
    const caregiverReference = parseReferenceTokens(caregiverName).find(
        (segment) =>
            segment.kind === 'reference' && segment.type === 'caregiver',
    );
    const caregiverId =
        caregiverReference?.kind === 'reference' ? caregiverReference.id : null;
    const resolvedName = useEntityReferenceLabel(
        caregiverId === null ? null : scope,
        'caregiver',
        caregiverId ?? '',
    );
    const initials = getAvatarInitials(
        caregiverId === null ? caregiverName : (resolvedName ?? ''),
    );
    return (
        <div
            className={cn(
                'w-full max-w-[65ch] min-w-0',
                CARD_SURFACES.assignment,
                RECEIPT_PADDING.assignment,
            )}
        >
            <section
                aria-labelledby={titleId}
                aria-busy={status === 'assigning'}
                data-state={status}
                className="relative w-full min-w-0"
            >
                <h3
                    id={titleId}
                    className="text-v3-chat mb-3 font-semibold wrap-anywhere"
                >
                    {pending
                        ? 'Assign '
                        : assigned
                          ? 'Assigned '
                          : status === 'assigning'
                            ? 'Assigning '
                            : 'Assignment for '}
                    <span>
                        <ResolvedReferenceText text={caregiverName} />
                    </span>
                    {pending ? '?' : null}
                </h3>
                <ul className="m-0 grid list-none gap-2 p-0">
                    {shifts.map((shift, index) => {
                        const shiftAssigned =
                            assigned ||
                            (status === 'partial' &&
                                shift.resultLabel === 'Assigned');
                        return (
                            <li
                                key={shift.id}
                                className="bg-muted flex min-h-[64px] min-w-0 items-center gap-2.5 rounded-[10px] px-3 py-2.5"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-v3-chat font-semibold wrap-anywhere">
                                            <ResolvedReferenceText
                                                text={shift.name}
                                            />
                                        </span>
                                        {shift.recurrenceLabel !==
                                            undefined && (
                                            <span
                                                title={shift.recurrenceLabel}
                                                className="text-foreground/70 text-v3-chat-meta inline-flex shrink-0 rounded-md border px-1.5 font-medium"
                                            >
                                                Recurring
                                                <span className="sr-only">
                                                    , {shift.recurrenceLabel}
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-foreground/70 text-v3-chat-meta mt-0.5 wrap-anywhere">
                                        <ResolvedReferenceText
                                            text={shift.detail}
                                        />
                                    </div>
                                    {shift.resultLabel !== undefined && (
                                        <div className="text-v3-chat-meta mt-1 font-medium">
                                            {shift.resultLabel}
                                        </div>
                                    )}
                                </div>
                                <motion.span
                                    aria-hidden="true"
                                    className="border-agent-action bg-agent-action flex size-7 shrink-0 items-center justify-center rounded-full border text-[10px] leading-none font-semibold text-white"
                                    initial={false}
                                    animate={{
                                        opacity: shiftAssigned ? 1 : 0,
                                        scale: shiftAssigned ? 1 : 0.85,
                                    }}
                                    transition={{
                                        duration: reduceMotion ? 0 : 0.45,
                                        delay: reduceMotion
                                            ? 0
                                            : Math.min(index, 4) * 0.07,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                >
                                    {shiftAssigned ? initials : null}
                                </motion.span>
                            </li>
                        );
                    })}
                </ul>
                {notices.length > 0 && (
                    <div className="mt-3">
                        <div className="text-foreground text-v3-chat font-semibold tracking-[-0.01em]">
                            Before you approve
                        </div>
                        <ul className="text-muted-foreground text-v3-chat mt-0.5 flex flex-col">
                            {notices.map((notice) => (
                                <li key={notice}>{notice}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className={assigned ? 'sr-only' : 'mt-3 min-h-9'}>
                    {pending ? (
                        <div className="inline-grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                disabled={disabled || shifts.length === 0}
                                onClick={onAssign}
                                className="bg-foreground text-background focus-visible:ring-ring text-v3-chat min-h-8 rounded-[9px] px-3 font-semibold focus-visible:ring-2 disabled:opacity-40"
                            >
                                {confirmLabel}
                            </button>
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={onDeny}
                                className="text-muted-foreground focus-visible:ring-ring text-v3-chat min-h-8 rounded-[9px] border px-3 font-semibold focus-visible:ring-2 disabled:opacity-40"
                            >
                                Deny
                            </button>
                        </div>
                    ) : (
                        <div
                            role="status"
                            className="text-muted-foreground text-v3-chat flex min-h-9 items-center gap-1.5 font-medium"
                        >
                            <ResolvedReferenceText
                                text={statusLabel ?? STATUS_LABELS[status]}
                            />
                        </div>
                    )}
                </div>
                {footer}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 overflow-hidden"
                >
                    {PARTICLES.map((particle, index) => (
                        <motion.span
                            key={index}
                            initial={false}
                            animate={
                                assigned && reduceMotion !== true
                                    ? {
                                          opacity: [0, 0.65, 0],
                                          x: [
                                              0,
                                              particle.drift / 2,
                                              particle.drift,
                                          ],
                                          y: [0, -12, 30],
                                          rotate: [0, particle.rotation],
                                      }
                                    : { opacity: 0, x: 0, y: 0, rotate: 0 }
                            }
                            transition={{
                                duration: 1.35,
                                delay:
                                    0.3 +
                                    particle.delay +
                                    Math.min(shifts.length - 1, 4) * 0.07,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            style={{
                                backgroundColor: particle.color,
                                left: particle.left,
                                top: particle.top,
                            }}
                            className="absolute h-[5px] w-[3px] rounded-[1px] opacity-0 motion-reduce:hidden"
                        />
                    ))}
                </div>
            </section>
            {pending && (
                <div
                    role="status"
                    className="text-muted-foreground text-v3-chat-meta mt-3 flex items-center gap-1.5"
                >
                    <FlowerMark className="text-agent-action size-3.5 shrink-0" />
                    Waiting for your approval…
                </div>
            )}
        </div>
    );
}
